import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { openManagedMatDialog } from '@go-modules/utilities';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { Subject } from 'rxjs';

export interface OptionalDatePickerDialogData {
	submitText: string;
	title: string;
	dialogTitleId?: string;
}

@Component({
	selector: 'optional-date-picker-dialog',
	templateUrl: './optional-date-picker-dialog.component.html',
	styleUrls: ['./optional-date-picker-dialog.component.scss'],
	standalone: false
})
export class OptionalDatePickerDialogComponent implements OnInit, OnDestroy {
	public readonly destroy$ = new Subject<void>();

	public form: UntypedFormGroup = new UntypedFormGroup({
		start_date: new UntypedFormControl(null),
		end_date: new UntypedFormControl(null)
	});

	public static open (
		dialog: MatDialog,
		config?: MatDialogConfig<OptionalDatePickerDialogData>
	): MatDialogRef<OptionalDatePickerDialogComponent, any> {
		const dialogTitleId = config?.data?.dialogTitleId ?? OptionalDatePickerDialogComponent.getDialogTitleId();

		return openManagedMatDialog(dialog, OptionalDatePickerDialogComponent, {
			...(config ?? {}),
			data: {
				...(config?.data ?? {}),
				dialogTitleId
			},
			ariaLabelledBy: dialogTitleId
		});
	}

	private static getDialogTitleId (): string {
		return `optional-date-picker-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	}

	private dateRangeValidator: ValidatorFn = (group: AbstractControl): { [key: string]: any } | null => {
		const start = group.get('start_date')?.value;
		const end = group.get('end_date')?.value;

		// Validate only if both dates are provided
		if (start && end && start > end) {
			return { dateRangeInvalid: true };
		}
		return null;
	};

	constructor (
		@Inject(MAT_DIALOG_DATA) public data: OptionalDatePickerDialogData,
		public dialogRef: MatDialogRef<OptionalDatePickerDialogComponent>
	) {
		this.data.dialogTitleId ??= OptionalDatePickerDialogComponent.getDialogTitleId();
	}

	public ngOnInit () {
		this.form.addValidators(this.dateRangeValidator);
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public dismiss () {
		this.dialogRef.close({ dismissed: true });
	}

	public submit (form) {
		if (form.invalid) {
			return;
		}

		this.dialogRef.close({
			start_date: form.controls.start_date.value,
			end_date: form.controls.end_date.value
		});
	}
}
